// 覚醒王の致命「跳び噛み2回 → 3回目が本命」(2026-10-09 夜に比較版で作り・10-09 夜「この形で進める」)。致命を弾ける仕組み (既定) の時の既定 = 配布版 (2026-10-10 exec-parry-ship)。DEV の綴りは ?awokenDevour=gladius。
//
// ユーザー原文 (10-09・見比べページ②の memo):「グラディウスの前に進みながら噛みつきを2回するような感じで
//   2回目までに攻撃判定はなく3回目で致命攻撃として作ってほしい。伝わるかな」
// 2回目の答え (10-09・窓口経由):「本攻撃とその前の３連ガブガブが全く動きとして違うから本攻撃の動きを３連ガブガブに寄せてほしい」
//   → r2 (この版): 3回目も1・2回目と同じ「低く構える → 前へ跳ぶ → 宙で口を開けて首を伸ばす → 宙で顎を閉じる」。
//     旧 r1 の「後ろ脚の沈み直し → 立ち上がって口を開けて止まる → 飛び込む」(extend_coil → rush_windup → exec_windup) は使わない。
//     3回目だけ構えが長く深く、跳びが少し大きい (距離・高さ・宙の長さ)。顎を閉じた瞬間 = 当たり (= active の頭)。
//   当たりは3回目だけ。紫から当たりまでのゲーム内の時間は r1 と同じ 470ms (押す時機の手がかりを変えない)。
//
// 合図 (致命の紫と一瞬の止まり) の比較 = DEV ?gladiusTell=purple|none|light (URL を読むのは game.ts の DEV の箱の中だけ・既定 purple = r1 と同じ合図)。
//   purple = 2回目の着地で構え直した所で紫が灯って 0.12 秒止まる (r1 と同じ)。
//   none   = 紫も止まりも出さない (3回目の長い構えだけが手がかり)。当たりの瞬間の紫も出さない。
//   light  = 3回目の跳びの頭から紫をほんのり (止まりは無し)。
//   動き・時間は3つとも同じ (止まりの 120ms だけ purple の実時間が長い)。
//
// 作り: 「灰を食む」(src/awoken_devour.ts) の段取りの入れ物 (AwokenDevourPlan) をそのまま使い、pounce を足した物を返す。
//   体の位置・絵の並び・音はこの段取りから作る。新しい絵は描いていない (今ある絵の組み合わせだけ)。
//   使う絵: 構え・着地 = bite_windup_deep / 宙で口を開けて首を伸ばす = bite_contact_up_0910 (採用絵 bite_contact_closed_0912 と同じ体で口だけ開いた 09-10 の絵)
//           / 宙で顎を閉じる = bite_contact (→ bite_contact_closed_0912) / 着地の手前 = bite_step (→ bite_step_closed_0912・顎を閉じたまま前脚を下ろす)
//           / 3回目の当たりの後 = bite_contact のまま噛みしめ → bite_step → bite_follow → bite_recover (配布版の暴噛の戻りと同じ並び)。
//   足の決まり: 床に着いている間は、横の倍率と前への描き位置を変えない (= 着いた足は床の上で動かない)。
//     絵の差し替えで足の位置が大きく変わる所 (構え ↔ 伸びた噛みの絵) は、すべて宙にいる間に置く。
//   壁を背にして下がれない時 (跳び噛み2回ぶんの間合いが取れない) は、見せの跳びを減らす (10-12 夕)・それでも近ければ壁ぎわの近い形 (10-14・planAwokenPounce)。
//
// ⚠️ この file は awoken_devour.ts から読まれる。逆向きに読むと循環するので、awoken_devour.ts は import しない
//   (「灰を食む」と揃える値は下に写し、tests/awoken_devour_pounce.test.ts が同じ値か照合する)。
// ⚠️ URL の値の綴り (比較版の名前) をこの file のコードに書かない (配布版に残る識別子・文字列から漏れないように・テストが見張る)。
import type { BossAttackTimeline, BossPoseKey, BossPoseMotion } from './boss_pose_timeline'
import { PLAYER } from './balance'
import { awokenMouthPoint } from './awoken_bite_mouth'
import { setStunCutDirectPaused, stunCutOn } from './stun_cut'

export const AWOKEN_POUNCE = {
  /** 当たり無しの跳び噛みの回数。3回目が本命。 */
  feints: 2,
  /** 1回目の構え (待機から頭を低くして口を開ける)。後ろ跳びの後もこの長さ。 */
  stanceMs: 220,
  /** 見せの跳び噛みの宙の長さ・弧の高さ。 */
  airMs: 150,
  heightPx: 12,
  /** 1回目の着地から 2回目の踏み切りまで。噛みの間隔 = airMs + crouchMs = 290ms (r1 と同じ)。 */
  crouchMs: 140,
  /** 宙の中の割り振り (見せの跳び): 0〜launch = 構えの絵のまま踏み切る / launch = 踏み切りの途中 (pounce_launchmid_1009・10-09 夜に追加)
   *  / reach = 口を開けて首を伸ばす (bite_contact_up_0910)
   *  / snap = 顎を閉じる (bite_contact) / step = 前脚を下ろし始める (bite_step) / land = 着地の構え (bite_windup_deep)。 */
  launchFrac: 0.06,
  reachFrac: 0.28,
  snapFrac: 0.42,
  stepFrac: 0.62,
  landFrac: 0.78,
  /** 1回の跳びで寄る距離 (目安・上限・下限)。間合いに合わせて伸び縮みする。 */
  stepPx: 55,
  stepMaxPx: 80,
  stepMinPx: 25,
  /** 見せの跳びの伸びた絵の前端 (角と顎の先) = 主人公の足先 (当たりの半幅) からこれだけ手前。足りない時は min まで縮める。 */
  toeGapPx: 35,
  toeGapMinPx: 20,
  // ---- 3回目 (本命) ----
  /** 2回目の着地から構え直すまで (ここで purple の合図)。 */
  settleMs: 80,
  /** 合図から3回目の踏み切りまで (低く深く構える・最後の stillMs は完全に止まる)。 */
  holdMs: 330,
  stillMs: 120,
  /** 3回目の宙の長さ・弧の高さ・口を開けて首を伸ばす所・顎を閉じる所 (= 当たり = active の頭)。
   *  合図 → 当たり = holdMs + finalAirMs × finalSnapFrac = 470ms (r1 の openMs 360 + lungeMs 110 と同じ)。 */
  finalAirMs: 200,
  finalHeightPx: 20,
  /** 壁ぎわの近い跳び (closeQuarters・10-14) の3回目の弧の高さ。前へ進めないので、上へ跳んで上から噛み下ろす形にする。 */
  closeFinalHeightPx: 36,
  finalLaunchFrac: 0.04,
  finalReachFrac: 0.2,
  finalSnapFrac: 0.7,
  /** 近い時の後ろ跳び (「灰を食む」と同じ拍・同じ絵)。旧 (DEV ?stunCut=0) はこの拍のまま。 */
  hop: { crouchMs: 90, airMs: 230, landMs: 110, heightPx: 28, heightRefPx: 150, maxHeightPx: 38, minPx: 20, refPx: 110, maxAirMs: 340 },
  /** 崩れてから致命が当たるまでを 1.8 秒以内にする速い拍 (10-12 昼の既定・同日夕に下の hopPace へ戻した＝いまは DEV ?awokenHopPace=fast だけ。
   *  元の注文＝10-12 ユーザー「固有のとどめの長い待ちを縮める」・src/stun_cut.ts)。
   *  近い時 (崩れた時の間合いはたいてい 110〜180px) の後ろ跳びが 0.54 秒＋構え 0.22 秒あり、間合い 120 で崩れてから当たるまで 1.92 秒だった。
   *  屈み 90→60・宙 230〜340→200〜240・着地 110→60・着地の後の構え 220→30 (着地の屈みがそのまま構えになる)。
   *  見せの跳び2回と3回目 (合図から当たりまで 470ms) は変えない。宙から降りる時 (大暴れ) だけ宙を dropExtraMs まで長くしてよい。 */
  hopFast: { crouchMs: 60, airMs: 200, maxAirMs: 240, landMs: 60, stanceAfterHopMs: 30, dropExtraMs: 60 },
  /** 既定の後ろ跳び (2026-10-12 夕・検収パック 10-12 F-2 の答え「速すぎる」で戻した)。屈み・宙・着地は前の拍 (hop) と同じ
   *  (屈み 90・宙 230〜340・着地 110)。着地の後の構えは stanceAfterHopMs (前 220 → 0: 着地の屈みがそのまま1回目の踏み切りの構え)。
   *  戻した分の時間は、覚醒王だけ打ち切りの構え直しを挟まず、技の途中からすぐ後ろ跳びの屈みへ入る形で取り返す
   *  (src/stun_cut.ts directByBoss)。崩れてから当たるまで 1.8 秒以内 (tests/stun_cut.test.ts)。
   *  前の速い拍 (hopFast) は DEV ?awokenHopPace=fast だけ (URL を読むのは game.ts の DEV の箱の中)。 */
  hopPace: { crouchMs: 90, airMs: 230, maxAirMs: 340, landMs: 110, stanceAfterHopMs: 0, dropExtraMs: 60 },
  /** 遠い時は構えの間に寄る (「灰を食む」と同じ)。 */
  approachFreePx: 30,
  approachMsPerPx: 1.5,
  approachMaxExtraMs: 350,
} as const

// 1枚ずつ長く映す並び (DEV の比較・URL を読むのは game.ts の DEV の箱の中・2026-10-25 docs/research/2026-10-25-st6-measured-polish.md)。
//   既定は宙の 150ms に絵が5枚あり、踏み切りの途中・首を伸ばす絵・着地の手前が 20〜33ms しか映らない (1〜2コマ)。
//   この並びでは、どの絵も 83ms (5コマ) 以上映す。絵の枚数は増やさず、短い絵を抜いて前後の絵へ時間を渡す:
//   見せの跳び = 構え → 踏み切りの途中 → 顎を閉じた絵 → 構え (首を伸ばす絵と着地の手前の絵を抜く)。
//   3回目 = 構え → 踏み切りの途中 → 首を伸ばす絵 → 当たり (顎を閉じる)。後ろ跳びから入る時は着地の絵から直に踏み切る (構えへ沈む 50ms を抜く)。
//   時間: 見せの跳びの宙 150 → 200・屈み 140 → 90 (噛みの間隔 290ms は同じ)・2回目の着地から合図まで 80 → 30 (合図の時刻は同じ)・
//   合図から3回目の踏み切りまで 330 → 294・踏み切りから当たりまで 140 → 176 (合図 → 当たり 470ms は同じ)。技の長さ・当たりの時刻は変わらない。
export const AWOKEN_POUNCE_LONG = {
  airMs: 200,
  crouchMs: 90,
  settleMs: 30,
  /** 見せの跳び: 踏み切りの途中 (launch)・顎を閉じた絵 (snap)・着地の構え (land) へ替わる所。 */
  launchFrac: 0.04,
  snapFrac: 0.475,
  landFrac: 0.9,
  holdMs: 294,
  finalAirMs: 240,
  finalLaunchFrac: 0.03,
  finalReachFrac: 92 / 240,
  finalSnapFrac: 176 / 240,
  /** 伸びた絵 (首を伸ばす・顎を閉じる) を後ろへ寄せて出してから、決まった描き位置へ戻すまで (ms)。 */
  settleOffMs: 40,
} as const
let longFrames = true
/** 1枚ずつ長く映す並びにするか (既定 true = 配布版・2026-10-27 作者「OK」で採用。false = 10-25 までの並び＝game.ts の DEV の切り替え)。 */
export function setPounceLongFrames(on: boolean): boolean {
  longFrames = on
  return longFrames
}
export function pounceLongFramesOn(): boolean { return longFrames }
/** 長く映す並びで、1枚の絵を最低これだけ映す (60fps の 5コマ)。 */
export const LONG_MIN_FRAME_MS = 250 / 3
/** 長く映す並びで、後ろ跳びの着地の絵から直に踏み切りの途中へ替える時の描き位置 (後ろ足が揃う所・tests/awoken_devour_pounce.test.ts で測る)。 */
export const LONG_FROM_LAND_LAUNCH_OFF = -20
/** いま使う跳びの時間の割り振り (既定 = AWOKEN_POUNCE・長く映す並びの時だけ AWOKEN_POUNCE_LONG で上書き)。 */
function pounceShape() {
  const D = AWOKEN_POUNCE
  return longFrames ? { ...D, ...AWOKEN_POUNCE_LONG } : D
}

/** 踏み切りの途中 (後ろ脚のつま先だけ床に残し、体が半分伸び、口を開けたまま前脚が床を離れた所・10-09 新規の絵)。 */
export const POUNCE_LAUNCH_IMAGE = 'pounce_launchmid_1009'
/** 宙で口を開けて首を伸ばす絵 (採用絵 bite_contact_closed_0912 と同じ体・同じ寸法で口だけ開いた 09-10 の絵)。 */
export const POUNCE_REACH_IMAGE = 'bite_contact_up_0910'

/** 絵の中心 (= ボスの x) から一番後ろの接地足・一番前の接地した爪・絵の前端 (角と顎の先) までの距離 (表示 186px・倍率 1・描き位置 0)。
 *  描く絵 (牙の版・採用絵の差し替え後) で実測 = tests/awoken_devour_pounce.test.ts が PNG から測った値と照合する。 */
export const AWOKEN_POUNCE_FEET: Readonly<Record<string, { rear: number; front: number; end: number }>> = {
  bite_windup_deep: { rear: 111.272, front: 147.159, end: 147.159 },
  bite_contact: { rear: 172.105, front: 155.036, end: 186.766 },   // bite_contact_closed_0912
}

/** 構え・着地 (bite_windup_deep) の描き方 = 「灰を食む」の構えと同じ (横 1.02 倍・前へ -6px)。後ろ跳びの着地の絵と後ろ足が揃う値。 */
export const POUNCE_CROUCH = { sx: 1.02, off: -6 } as const
/** 宙で首を伸ばした絵・顎を閉じた絵 (bite_contact_up_0910 / bite_contact) の描き方。前端・口の位置の計算もこの値で行う。 */
export const POUNCE_SNAP = { sx: 1.04, off: -8 } as const
/** 3回目に顎を閉じた瞬間 (当たり) の描き方 = active の頭のキー。口の位置 (当たり) はこの値から出す。 */
export const POUNCE_FINAL_SNAP: BossPoseMotion = { offsetX: POUNCE_SNAP.off, offsetY: 0, scaleX: POUNCE_SNAP.sx + 0.02, scaleY: 0.97, rot: 1.2 }

/** 段取りの中の pounce の分 (「灰を食む」の段取りに足す)。 */
export type AwokenPounceExtra = {
  /** 見せの跳び 1回で寄る距離・伸びた絵の前端と足先の間 (この回の値)。 */
  stepPx: number
  toeGapPx: number
  /** 見せの跳び k (0 始まり) の踏み切りの時刻 (溜めの頭から ms)。 */
  launchMs: number[]
  /** 3回目の跳びで寄る距離 (着地まで)・顎を閉じた瞬間の間合い (口の中心が主人公の中心に来る)。 */
  finalStepPx: number
  contactGapPx: number
  /** 後ろ跳びの屈み・着地の長さ (この回の値・宙から降りる時は屈み 0)・宙から降りる時の始まりの高さ (0 = 床から跳ぶ)。 */
  hopCrouchMs: number
  hopLandMs: number
  dropFromPx: number
  /** 壁ぎわで主人公が口の届く所より内側にいる時の形 (10-14 試遊 C-3)。3回目は前へ進まず上へ跳んで噛み下ろし、
   *  当たりは口の周りに体の前1/3 を足す (普段の噛みと同じ・src/hits.ts)。 */
  closeQuarters?: boolean
}

/** 「灰を食む」の段取りと同じ形 (awoken_devour.ts の AwokenDevourPlan と同じ並び。pounce がある物がこの版)。
 *  この版での意味: lastBiteGapPx = 2回目の着地の間合い / landingGapPx = 3回目の着地の間合い / coilStartMs = 2回目の着地 /
 *  signalMs = 合図 (構え直した所) / lungeStartMs = 3回目の踏み切り / chargeMs = 3回目に顎を閉じる (当たり)。 */
export type AwokenPouncePlanShape = {
  bites: 2 | 3 | 4
  biteMs: number[]
  startGapPx: number
  biteStartGapPx: number
  lastBiteGapPx: number
  landingGapPx: number
  hopAirMs: number
  stanceMs: number
  biteStartMs: number
  coilStartMs: number
  signalMs: number
  lungeStartMs: number
  chargeMs: number
  pounce?: AwokenPounceExtra
}
type PouncePlan = AwokenPouncePlanShape & { pounce: AwokenPounceExtra }

// ===== 合図の比較 (DEV ?gladiusTell・URL は game.ts の DEV の箱で読む) =====
export type PounceTell = 'purple' | 'none' | 'light'
let tell: PounceTell = 'purple'
/** URL の値から切り替える ('none' | 'light'・それ以外 = purple = 今のまま)。 */
export function setPounceTell(value: string | null | undefined): PounceTell {
  tell = value === 'none' || value === 'light' ? value : 'purple'
  return tell
}
export function pounceTell(): PounceTell { return tell }
/** light の紫の強さ (3回目の踏み切りから当たりまで・ほんのり)。 */
export const POUNCE_LIGHT_AURA = { from: 0.22, to: 0.4 } as const

// 紫の出始め (2026-10-14 試遊 next-play-1014 C-3 のメモ「ボスの致命に関してはモーションにはいった次点で紫にしてください。
//   覚醒王であれば下がってるタイミングで既に紫のオーラを纏わせてください」)。
//   既定 = 溜めの頭 (後ろ跳びの屈み) から薄く纏い (wear)、合図 (構え直した所・一瞬の止まり) で一段強まって (signalFrom) 当たりへ濃くなる。
//   紫は「いま押す」の手がかりも兼ねていたので、押す時機は「強まる＋止まり」の段で残す (合図から当たりまで 470ms は同じ)。
//   前 (DEV ?execAura=old・URL を読むのは game.ts の DEV の箱の中) = 合図まで紫なし・合図で 0.35 から濃くなる。
export const POUNCE_EARLY_AURA = { wear: 0.3, signalFrom: 0.7 } as const
let earlyAuraOld = false
/** URL の値から切り替える。'old' = 前 (合図まで紫を出さない)。それ以外 (指定なし) = 既定 (溜めの頭から薄く纏う)。 */
export function setExecAuraEarly(value: string | null | undefined): boolean {
  earlyAuraOld = value === 'old'
  return earlyAuraOld
}
/** 溜めの頭から紫を纏うか (既定 true)。 */
export function execAuraEarlyOn(): boolean { return !earlyAuraOld }

/** 溜めの間の紫の強さ (null = 出さない)。 */
export function pounceAuraK(plan: AwokenPouncePlanShape, telegraphElapsedMs: number): number | null {
  if (tell === 'none') return null
  if (tell === 'light') {
    if (telegraphElapsedMs < plan.lungeStartMs) return null
    const p = Math.min(1, (telegraphElapsedMs - plan.lungeStartMs) / Math.max(1, plan.chargeMs - plan.lungeStartMs))
    return POUNCE_LIGHT_AURA.from + (POUNCE_LIGHT_AURA.to - POUNCE_LIGHT_AURA.from) * p
  }
  if (telegraphElapsedMs < plan.signalMs) return earlyAuraOld ? null : POUNCE_EARLY_AURA.wear
  const p = Math.min(1, (telegraphElapsedMs - plan.signalMs) / Math.max(1, plan.chargeMs - plan.signalMs))
  const from = earlyAuraOld ? 0.35 : POUNCE_EARLY_AURA.signalFrom
  return from + (1 - from) * p
}
/** 合図の一瞬の止まりを出すか (purple だけ)。 */
export function pounceSignalFreezes(): boolean { return tell === 'purple' }
/** 当たりの瞬間 (active) の紫を出すか (none だけ出さない)。 */
export function pounceActiveAura(): boolean { return tell !== 'none' }

/** 宙の中の進み (0〜1)。踏み切りが速く、着地は柔らかく。 */
export function pounceTravel(q: number): number {
  const p = Math.max(0, Math.min(1, q))
  return 0.35 * p + 0.65 * (1 - (1 - p) * (1 - p))
}

/** 跳び終わりの間合い L で、伸びた絵の前端が一番前へ出る時 (step の直前) の、前端と主人公の足先の間。 */
function snapToeGap(lastGapPx: number, stepPx: number): number {
  const F = AWOKEN_POUNCE_FEET.bite_contact!
  // 伸びた絵から替わる時刻 (既定 = 着地の手前の絵へ替わる所・長く映す並び = 着地の構えへ替わる所)。
  const atSwitch = lastGapPx + stepPx * (1 - pounceTravel(longFrames ? AWOKEN_POUNCE_LONG.landFrac : AWOKEN_POUNCE.stepFrac))
  return atSwitch - POUNCE_SNAP.sx * F.end - POUNCE_SNAP.off - PLAYER.width / 2
}
/** 足先との間を toeGap にする、2回目の跳び終わりの間合い。 */
export function awokenPounceLastGapPx(stepPx: number, toeGapPx: number): number {
  return toeGapPx - snapToeGap(0, stepPx)
}
/** 3回目に顎を閉じた瞬間の間合い = 閉じた口の中心 (当たりの口) が主人公の中心に来る所。 */
export function awokenPounceContactGapPx(): number {
  return awokenMouthPoint('bite_contact', POUNCE_FINAL_SNAP)!.forwardPx
}

/** 見せの跳びを減らした時、3回目 (本命) の跳びに残す最短の距離 (px・構えた所から顎を閉じる所まで)。 */
export const POUNCE_NO_ROOM_MIN_FINAL_PX = 30

/**
 * この回の段取りを決める。startGapPx = いまの間合い、roomBehindPx = 主人公から見てボスの後ろの壁 (ボスの体が止まる所) までの距離。
 * 下がれない (跳び噛み2回ぶんの間合いが取れない) 時:
 *   既定 (2026-10-12 夕・検収パック 10-12 F-2 のメモ「後ろに下がらずに直接紫を出したこともあった」) = 見せの跳びを 1 回、
 *     それでも足りなければ 0 回に減らし、下がれるだけ (壁まで) 後ろへ跳んでから同じ形 (構え → 紫 → 3回目の跳び) で噛む。
 *     背中が壁に着いていて下がる余地が 20px 未満の時だけ、後ろへ跳ばずにその場で構えて紫を出す (下がる所が無い)。
 *   10-14 (試遊 next-play-1014 C-3「まだ出た」・記録 playlogs/2026-10-14/045259-crowned-king-awoken.json の F の印 2 つの直前):
 *     主人公が口の届く所 (中心から約 165px) より内側にいて、背中の余地も 195px 未満だと、3回目の跳びの距離が取れず暴噛・終 (後ろへ下がらず
 *     紫が灯って 0.47 秒で当たる) になっていた。既定はここでも段取りを作る (closeQuarters): 壁まで (下がれなければその場で上へ) 後ろ跳び
 *     → 低く構える → 紫が強まる → 3回目は前へ進まず上へ跳んで噛み下ろす。当たりは口の周り＋体の前1/3 (普段の噛みと同じ)。
 *     主人公の中心がボスの体の前1/3 より内側 (CLOSE_MIN_GAP_PX 未満) の時だけ、今までどおり暴噛・終。
 *   10-12 夕の版 (DEV ?pounceNoRoom=1012) = 上の近い時は null (= 暴噛・終)。
 *   前 (DEV ?pounceNoRoom=old・旧 ?stunCut=0) = null (= 暴噛・終へ替える。紫が灯ってすぐ当たる別の技になっていた)。
 */
export function planAwokenPounce(startGapPx: number, roomBehindPx: number, startAltitudePx = 0): PouncePlan | null {
  const full = planPounceWith(AWOKEN_POUNCE.feints, startGapPx, roomBehindPx, startAltitudePx)
  if (full || noRoomOld || !stunCutOn()) return full
  for (let n = AWOKEN_POUNCE.feints - 1; n >= 0; n--) {
    const p = planPounceWith(n, startGapPx, roomBehindPx, startAltitudePx)
    if (p) return p
  }
  if (noRoom1012) return null
  return planPounceWith(0, startGapPx, roomBehindPx, startAltitudePx, true)
}

/** 壁ぎわの近い形 (closeQuarters) を作る最短の間合い = 主人公の当たりの半幅がボスの体の前1/3 (boss.x から約 69px 前) に掛かる所＋余り。
 *  これより近い (体が重なる) 時は暴噛・終。値は src/awoken_bite_mouth.ts の AWOKEN_BITE_FRONT_THIRD と照合する (tests/awoken_devour_pounce.test.ts)。 */
export const POUNCE_CLOSE_MIN_GAP_PX = 80

function planPounceWith(n: number, startGapPx: number, roomBehindPx: number, startAltitudePx: number, close = false): PouncePlan | null {
  const D = pounceShape()
  // 後ろ跳びの拍: 既定 (崩れたら打ち切る・src/stun_cut.ts) は hopPace / 旧 (DEV ?stunCut=0) は前の拍。
  const H = pounceHop()
  const contactGapPx = awokenPounceContactGapPx()
  let toeGapPx: number = D.toeGapPx
  let stepPx: number = n > 0 ? D.stepPx : 0
  let lastBiteGapPx = n > 0 ? awokenPounceLastGapPx(stepPx, toeGapPx)
    : Math.min(roomBehindPx, awokenPounceLastGapPx(D.stepPx, D.toeGapPx))
  if (close) {
    // 壁ぎわの近い形: 下がれるだけ (壁まで) 下がって構える。主人公より近くへは寄らない。
    lastBiteGapPx = Math.max(startGapPx, lastBiteGapPx)
    if (lastBiteGapPx < POUNCE_CLOSE_MIN_GAP_PX) return null
  } else if (n === 0 && lastBiteGapPx - contactGapPx < POUNCE_NO_ROOM_MIN_FINAL_PX) return null
  let ideal = lastBiteGapPx + n * stepPx
  if (n > 0 && ideal > roomBehindPx) {
    // 壁を背にしている: まず跳びを縮め、それでも足りなければ足先との間を縮める。それでも足りなければ作らない。
    stepPx = Math.max(D.stepMinPx, (roomBehindPx - lastBiteGapPx) / n)
    lastBiteGapPx = awokenPounceLastGapPx(stepPx, toeGapPx)
    if (lastBiteGapPx + n * stepPx > roomBehindPx) {
      toeGapPx = Math.max(D.toeGapMinPx, toeGapPx - (lastBiteGapPx + n * stepPx - roomBehindPx))
      lastBiteGapPx = awokenPounceLastGapPx(stepPx, toeGapPx)
    }
    if (lastBiteGapPx + n * stepPx > roomBehindPx + 1e-6) return null
    ideal = lastBiteGapPx + n * stepPx
  }
  let biteStartGapPx = ideal
  let hopAirMs = 0
  let approachPx = 0
  const fromAir = startAltitudePx > 0
  if (fromAir) {
    // 宙で崩れを見た (大暴れの壁・跳び): 跳びの宙から、見せの跳びの始まりの間合いへ降りる (屈まずに宙から入る)。
    //   降りる高さぶん宙を長く (高さ 200px で上限)。
    const byGap = H.airMs * Math.abs(ideal - startGapPx) / D.hop.refPx
    const byFall = H.airMs * Math.sqrt(startAltitudePx / 100)
    hopAirMs = Math.min(H.maxAirMs + H.dropExtraMs, Math.max(H.airMs, byGap, byFall))
  } else if (close) {
    // 壁ぎわの近い形: 下がる距離が小さくても (0 でも) 後ろ跳びの屈み・宙・着地を必ず見せる (「下がらずに直接紫」にしない)。
    hopAirMs = Math.min(H.maxAirMs, Math.max(H.airMs, H.airMs * (ideal - startGapPx) / D.hop.refPx))
  } else if (startGapPx < ideal - D.hop.minPx) {
    // 近い: 後ろ跳びで間を取る (「灰を食む」と同じ)。
    hopAirMs = Math.min(H.maxAirMs, Math.max(H.airMs, H.airMs * (ideal - startGapPx) / D.hop.refPx))
  } else if (startGapPx <= ideal + D.approachFreePx) {
    // ほぼ合っている: その場から跳ぶ。跳びの距離で合わせる (見せの跳びが無い時は、その場が3回目の構えの所)。
    biteStartGapPx = startGapPx
    if (n > 0) stepPx = (startGapPx - lastBiteGapPx) / n
    else lastBiteGapPx = startGapPx
  } else if (n === 0) {
    // 遠い (見せの跳びが無い時): 構えの間に寄る。
    approachPx = startGapPx - ideal
  } else {
    // 遠い: まず跳びを伸ばし (上限まで)、それでも遠い分は構えの間に寄る。
    stepPx = Math.min(D.stepMaxPx, (startGapPx - awokenPounceLastGapPx(D.stepMaxPx, toeGapPx)) / n)
    lastBiteGapPx = awokenPounceLastGapPx(stepPx, toeGapPx)
    biteStartGapPx = lastBiteGapPx + n * stepPx
    approachPx = startGapPx - biteStartGapPx
  }
  if (!close && n === 0 && lastBiteGapPx - contactGapPx < POUNCE_NO_ROOM_MIN_FINAL_PX) return null
  const hopCrouchMs = fromAir ? 0 : H.crouchMs
  const hopMs = hopAirMs > 0 ? hopCrouchMs + hopAirMs + H.landMs : 0
  const stanceMs = (hopAirMs > 0 ? H.stanceAfterHopMs : D.stanceMs)
    + Math.min(D.approachMaxExtraMs, Math.max(0, Math.abs(approachPx) - D.approachFreePx) * D.approachMsPerPx)
  const biteStartMs = hopMs + stanceMs
  const launchMs = Array.from({ length: n }, (_, k) => biteStartMs + k * (D.airMs + D.crouchMs))
  const coilStartMs = n > 0 ? launchMs[n - 1]! + D.airMs : biteStartMs
  // 見せの跳びが無い時は宙が長くなった分が無いので、合図までは既定の長さのまま (技の長さを変えない)。
  const signalMs = coilStartMs + (n > 0 ? D.settleMs : AWOKEN_POUNCE.settleMs)
  const lungeStartMs = signalMs + D.holdMs
  const chargeMs = lungeStartMs + D.finalAirMs * D.finalSnapFrac
  const biteMs = launchMs.map((_, k) => (k < n - 1 ? D.airMs + D.crouchMs : D.airMs))
  // 3回目: 顎を閉じる時 (宙の finalSnapFrac) に口が主人公の中心へ来るよう、跳び全体の距離を決める (その後の宙で少しだけ食い込む)。
  //   壁ぎわの近い形は前へ進まない (0 未満にしない = 壁へ下がりながら噛まない)。
  const finalStepPx = Math.max(close ? 0 : -Infinity, (lastBiteGapPx - contactGapPx) / pounceTravel(D.finalSnapFrac))
  const landingGapPx = lastBiteGapPx - finalStepPx
  return { bites: 2, biteMs, startGapPx, biteStartGapPx, lastBiteGapPx, landingGapPx, hopAirMs, stanceMs,
    biteStartMs, coilStartMs, signalMs, lungeStartMs, chargeMs,
    // 足先との間はこの回の跳びの距離で少し動く (その場から跳ぶ時)。実際の値を持たせる。
    pounce: { stepPx, toeGapPx: snapToeGap(lastBiteGapPx, stepPx), launchMs, finalStepPx, contactGapPx,
      hopCrouchMs, hopLandMs: H.landMs, dropFromPx: fromAir ? startAltitudePx : 0, ...(close ? { closeQuarters: true } : null) } }
}

function easeInOut(t: number): number {
  const p = Math.max(0, Math.min(1, t))
  return p < 0.5 ? 2 * p * p : 1 - 2 * (1 - p) * (1 - p)
}

// 後ろ跳びの拍の比較 (DEV ?awokenHopPace=fast = 10-12 昼の速い拍・URL を読むのは game.ts の DEV の箱の中)。
let fastPace = false
/** URL の値から切り替える。'fast' = 10-12 昼の速い拍 (hopFast)。それ以外 (指定なし) = 既定 (hopPace = 前の拍の速さ)。 */
export function setPounceHopFast(value: string | null | undefined): boolean {
  fastPace = value === 'fast'
  setStunCutDirectPaused(fastPace)   // 10-12 昼と同じく、覚醒王も構え直しを挟む (src/stun_cut.ts stunCutDirect)
  return fastPace
}
export function pounceHopFastOn(): boolean { return fastPace }

// 壁を背にして跳び噛み2回ぶんの間合いが取れない時の比較 (DEV ?pounceNoRoom=old = 前 = 暴噛・終へ替える / =1012 = 10-12 夕の版 = 近い時だけ暴噛・終)。
let noRoomOld = false
let noRoom1012 = false
/** URL の値から切り替える。'old' = 前 (段取りを作らず暴噛・終)・'1012' = 10-12 夕 (口の届く所より内側の時だけ暴噛・終)。
 *  それ以外 (指定なし) = 既定 (見せの跳びを 1 回・0 回に減らして同じ形で跳ぶ・近い時は壁ぎわの近い形)。 */
export function setPounceNoRoom(value: string | null | undefined): boolean {
  noRoomOld = value === 'old'
  noRoom1012 = value === '1012'
  return noRoomOld
}

/** いま使う後ろ跳びの拍 (既定 = 前の拍の速さ hopPace・DEV ?awokenHopPace=fast = 10-12 昼の速い拍・旧 DEV ?stunCut=0 = 前の拍と構え 220)。 */
export function pounceHop(): { crouchMs: number; airMs: number; maxAirMs: number; landMs: number; stanceAfterHopMs: number; dropExtraMs: number } {
  const D = AWOKEN_POUNCE
  return stunCutOn() ? (fastPace ? D.hopFast : D.hopPace)
    : { crouchMs: D.hop.crouchMs, airMs: D.hop.airMs, maxAirMs: D.hop.maxAirMs, landMs: D.hop.landMs, stanceAfterHopMs: D.stanceMs, dropExtraMs: 0 }
}

export function awokenPounceHopHeightPx(plan: AwokenPouncePlanShape): number {
  const h = AWOKEN_POUNCE.hop
  const dist = Math.abs(plan.biteStartGapPx - plan.startGapPx)
  return Math.min(h.maxHeightPx, h.heightPx * Math.max(1, dist / h.heightRefPx))
}

/** 3回目の着地の時刻 (溜めの頭から ms・当たりの後 = active の中)。 */
export function awokenPounceFinalLandMs(plan: AwokenPouncePlanShape): number {
  return plan.lungeStartMs + pounceShape().finalAirMs
}

/** 溜めの頭から elapsedMs の時の、ボスと主人公の間合い (px) と体の高さ。chargeMs を越えた分 (active の頭) は3回目の宙の続き。 */
export function awokenPounceGapAt(plan: PouncePlan, elapsedMs: number): { gapPx: number; altitudePx: number } {
  const D = pounceShape()
  const t = elapsedMs
  const crouchMs = plan.pounce.hopCrouchMs, landMs = plan.pounce.hopLandMs, drop = plan.pounce.dropFromPx
  if (plan.hopAirMs > 0 && t < crouchMs + plan.hopAirMs + landMs) {
    const p = (t - crouchMs) / plan.hopAirMs
    const pc = Math.max(0, Math.min(1, p))
    // 宙から降りる時 (drop > 0): 始まりの高さから落ちながら (始めはゆっくり・床の手前で速く)、小さな弧を足す。
    const arc = p > 0 && p < 1 ? awokenPounceHopHeightPx(plan) * Math.sin(Math.PI * p) * (drop > 0 ? 0.5 : 1) : 0
    return { gapPx: plan.startGapPx + (plan.biteStartGapPx - plan.startGapPx) * easeInOut(pc),
      altitudePx: (p < 1 ? drop * (1 - pc * pc) : 0) + arc }
  }
  if (t < plan.biteStartMs) {
    if (plan.hopAirMs > 0) return { gapPx: plan.biteStartGapPx, altitudePx: 0 }
    const p = (t - (plan.biteStartMs - plan.stanceMs)) / plan.stanceMs
    return { gapPx: plan.startGapPx + (plan.biteStartGapPx - plan.startGapPx) * easeInOut(p), altitudePx: 0 }
  }
  if (t >= plan.lungeStartMs) {
    // 3回目 (本命): 見せの跳びと同じ形の弧 (少し高く・長く)。顎を閉じた後 (active) も着地まで宙を進む。
    const q = (t - plan.lungeStartMs) / D.finalAirMs
    if (q >= 1) return { gapPx: plan.landingGapPx, altitudePx: 0 }
    return { gapPx: plan.lastBiteGapPx - plan.pounce.finalStepPx * pounceTravel(q),
      altitudePx: q > 0 ? (plan.pounce.closeQuarters ? D.closeFinalHeightPx : D.finalHeightPx) * Math.sin(Math.PI * q) : 0 }
  }
  const { stepPx, launchMs } = plan.pounce
  for (let k = launchMs.length - 1; k >= 0; k--) {
    const L = launchMs[k]!
    if (t < L) continue
    const from = plan.biteStartGapPx - stepPx * k
    if (t < L + D.airMs) {
      const q = (t - L) / D.airMs
      return { gapPx: from - stepPx * pounceTravel(q), altitudePx: q > 0 && q < 1 ? D.heightPx * Math.sin(Math.PI * q) : 0 }
    }
    return { gapPx: from - stepPx, altitudePx: 0 }
  }
  return { gapPx: plan.biteStartGapPx, altitudePx: 0 }
}

/** 見せの跳び k の、口を開けて首を伸ばす時刻・顎を閉じる時刻 (宙で bite_contact に替わる)・着地の時刻。 */
export function awokenPounceTimes(plan: PouncePlan): { launchMs: number; reachMs: number; snapMs: number; landMs: number }[] {
  const D = pounceShape()
  // 長く映す並びの見せの跳びは首を伸ばす絵を抜く (reach = snap = 顎を閉じた絵へ替わる所)。
  if (longFrames) return plan.pounce.launchMs.map(L => ({ launchMs: L, reachMs: L + D.airMs * D.snapFrac, snapMs: L + D.airMs * D.snapFrac, landMs: L + D.airMs }))
  return plan.pounce.launchMs.map(L => ({ launchMs: L, reachMs: L + D.airMs * D.reachFrac, snapMs: L + D.airMs * D.snapFrac, landMs: L + D.airMs }))
}

/** この tick で跨いだ見せの跳びの着地 (足が床を打つ音を出す)。 */
export function awokenPounceLandsCrossed(plan: AwokenPouncePlanShape, prevElapsedMs: number, nextElapsedMs: number): number[] {
  const out: number[] = []
  if (!plan.pounce) return out
  awokenPounceTimes(plan as PouncePlan).forEach((b, k) => { if (prevElapsedMs < b.landMs && nextElapsedMs >= b.landMs) out.push(k) })
  return out
}

// ===== 絵の並び =====
type Motion = Pick<BossPoseKey, 'offset' | 'scale' | 'rot' | 'ease' | 'hold' | 'tag'>
const HOLD = { hold: true } as const
const timelineCache = new WeakMap<object, BossAttackTimeline>()

/** 致命 (暴噛・終) の当たりの長さ (attacks.ts の kingExecutionAwoken.activeMs と同じ・テストが照合する)。active のキーの割合の基準。 */
export const POUNCE_FINISHER_ACTIVE_MS = 250

/** 宙で絵を替える時の前への描き位置 (頭が一度に跳びすぎないよう、替わった絵を少し前/後ろへずらしてから決まった値へ戻す)。 */
export const POUNCE_SWITCH_OFF = {
  /** 構え → 踏み切りの途中: 出す所の描き位置と、首を伸ばす絵へ替わる直前の描き位置。 */
  launchFrom: -20,
  launchTo: -12,
  /** 踏み切りの途中 → 口を開けて首を伸ばす (半分伸びた体 → 伸びた体): 伸びた絵を後ろへ寄せて出す。 */
  reachFrom: -44,
  /** 顎を閉じた絵 → 着地の手前 (bite_step・頭が少し後ろへ戻る): 前へ寄せて出す。 */
  stepFrom: 14,
  /** 着地の手前 → 着地の構え: 構えの絵を前へ寄せて出し、着地までに planted の値へ。 */
  crouchFrom: 30,
} as const

/** 長く映す並びの見せの跳びで、踏み切りの途中の次に映す顎を閉じた絵 (前脚を下ろしかけた絵 = 伸びた体と構えの間の体)。
 *  伸びた bite_contact を使うと構えへ替わる所で頭が 57px・後ろ足が 106px 跳んだ。bite_step なら頭 42px・後ろ足 43px (配布版の宙の替わりの最大 47 / 65px 以下)。
 *  描き位置は 2026-10-25 に間合い 110/240/341 で総当たりに測って決めた (from = 出す所・to = 着地の構えへ替わる直前・crouchFrom = 構えを出す所)。 */
export const LONG_SNAP_IMAGE = 'bite_step'
export const LONG_SNAP_OFF = { from: -20, mid: -20, to: -20, sx: 1.0, crouchFrom: -10 } as const

/** この回の段取りの絵の並び。active と recovery もこの版の並び (3回目の顎を閉じた絵のまま噛みしめ → 暴噛の戻り)。 */
export function awokenPounceTimeline(plan: PouncePlan, _base: BossAttackTimeline): BossAttackTimeline {
  const cached = timelineCache.get(plan)
  if (cached) return cached
  const D = pounceShape()
  const long = longFrames
  const keys: BossPoseKey[] = []
  const at = (ms: number) => Math.max(0, Math.min(0.999999, ms / plan.chargeMs))
  const key = (ms: number, image: string, m: Motion = {}) => keys.push({ phase: 'telegraph', at: at(ms), image, ...HOLD, ...m })
  const W = POUNCE_CROUCH, S = POUNCE_SNAP
  let t = 0
  if (plan.hopAirMs > 0) {
    // 宙から降りる時 (大暴れの壁・跳びで崩れを見た) は屈まずに宙の絵から入る (hopCrouchMs = 0)。
    if (plan.pounce.hopCrouchMs > 0) key(0, 'back_hop_crouch_1001', { ease: 'easeIn' })
    key(plan.pounce.hopCrouchMs, 'back_hop_air_1001', { ease: 'easeInOut' })
    t = plan.pounce.hopCrouchMs + plan.hopAirMs
    key(t, 'back_hop_land_1001', { offset: { x: 8, y: 3 }, scale: { x: 1.03, y: 0.96 }, ease: 'step' })
    t += plan.pounce.hopLandMs
  }
  // 構え: 頭を低くして口を開ける (跳ばなかった時は待機の絵から入る)。踏み切りの直前に一段沈む。
  //   待機の絵は描き位置・倍率を動かさずに保つ (動かすと待機の後ろ足が床を滑る)。構えの絵へ替わる所で一度に沈む。
  //   跳んだ後の構えが短い時 (既定の速い拍・40ms) は、着地の絵からそのまま踏み切りの直前の沈みへ入る (キーの順を守る)。
  if (plan.hopAirMs === 0) key(t, 'idle', { ease: 'step' })
  // 長く映す並びで、後ろ跳びの着地から構えまでが 5コマに満たない時は、構えの絵を挟まず着地の絵から直に踏み切る (見せの跳びがある時)。
  //   見せの跳びが無い時 (n = 0) は着地の後に長い構えがあるので、着地の絵を全部 (hopLandMs) 見せてから構えへ替える。
  const fromLandDirect = long && plan.hopAirMs > 0 && plan.pounce.launchMs.length > 0 && plan.stanceMs < LONG_MIN_FRAME_MS
  if (!fromLandDirect) {
    const stanceKey = t + Math.min(110, plan.stanceMs * 0.4)
    // 跳んだ後の構えが 0 の時 (既定 hopPace・10-12 夕): 着地の終わり 50ms を構えの沈みにする (着地の絵 → 踏み切りの途中が1コマで跳ばない・
    //   着地の屈みを見せる時間は 60ms 残る)。
    const sinkKey = plan.hopAirMs > 0 && plan.stanceMs <= 0
      ? (long ? t : t - Math.min(50, plan.pounce.hopLandMs * 0.45))
      : Math.max(stanceKey, plan.biteStartMs - 50)
    if (sinkKey > stanceKey) key(stanceKey, 'bite_windup_deep', { offset: { x: W.off, y: 3 }, scale: { x: W.sx, y: 0.97 }, ease: 'easeInOut' })
    key(sinkKey, 'bite_windup_deep', { offset: { x: W.off, y: 6 }, scale: { x: W.sx, y: 0.93 }, rot: 0.6, ease: 'easeIn' })
  }

  /** 踏み切り → 口を開けて首を伸ばす (見せの跳びと3回目で同じ形・3回目は reach を長く持つ)。
   *  withReach = false: 首を伸ばす絵を抜き、踏み切りの途中から顎を閉じた絵へ替える (長く映す並びの見せの跳び)。
   *  fromLand = true: 構えの絵を挟まず、後ろ跳びの着地の絵から直に踏み切りの途中へ替える。 */
  const launchAndReach = (L: number, A: number, launchFrac: number, reachFrac: number, deep: boolean, withReach = true, fromLand = false) => {
    // 踏み切り: 口を開けたまま床を蹴る (体を少し伸ばして鼻先を上げる)。描き位置は替わる直前まで動かさない。
    if (!fromLand) {
      key(L, 'bite_windup_deep', { offset: { x: W.off, y: 2 }, scale: { x: W.sx, y: 1.0 }, rot: deep ? -2.0 : -1.5, ease: 'linear' })
      key(L + A * launchFrac - 1, 'bite_windup_deep', { offset: { x: W.off, y: 1 }, scale: { x: W.sx, y: 1.0 }, rot: deep ? -2.5 : -2.0, ease: 'step' })
    }
    // 踏み切りの途中 (後ろ脚のつま先だけ床に残し、体が半分伸びる)。替わった所の描き位置から、首を伸ばす絵へ替わる直前の値へ流す。
    key(L + A * launchFrac, POUNCE_LAUNCH_IMAGE, { offset: { x: fromLand ? LONG_FROM_LAND_LAUNCH_OFF : POUNCE_SWITCH_OFF.launchFrom, y: 0 }, scale: { x: 1.0, y: 1.0 }, rot: -1.5, ease: 'linear' })
    key(L + A * reachFrac - 1, POUNCE_LAUNCH_IMAGE, { offset: { x: POUNCE_SWITCH_OFF.launchTo, y: 0 }, scale: { x: 1.0, y: 1.0 }, rot: -1.5, ease: 'step' })
    if (!withReach) return
    // 宙で口を開けたまま首を前へ伸ばす (伸びた体)。後ろへ寄せて出し、すぐ決まった描き位置へ。
    key(L + A * reachFrac, POUNCE_REACH_IMAGE, { offset: { x: POUNCE_SWITCH_OFF.reachFrom, y: 0 }, scale: { x: S.sx, y: 0.98 }, rot: -1.2, ease: 'linear' })
  }

  const times = awokenPounceTimes(plan)
  if (long) {
    // 長く映す並びの見せの跳び: 構え → 踏み切りの途中 → 顎を閉じた絵 → 着地の構え (各 85ms 以上)。
    const A = D.airMs, settle = AWOKEN_POUNCE_LONG.settleOffMs
    times.forEach((b, k) => {
      launchAndReach(b.launchMs, A, D.launchFrac, D.snapFrac, false, false, k === 0 && fromLandDirect)
      // 顎を閉じて前脚を下ろしかけた絵 (LONG_SNAP_IMAGE): 測って決めた描き位置のまま着地の手前まで持つ。
      key(b.snapMs, LONG_SNAP_IMAGE, { offset: { x: LONG_SNAP_OFF.from, y: 0 }, scale: { x: LONG_SNAP_OFF.sx, y: 0.98 }, rot: 0.6, ease: 'linear' })
      key(b.snapMs + settle, LONG_SNAP_IMAGE, { offset: { x: LONG_SNAP_OFF.mid }, scale: { x: LONG_SNAP_OFF.sx, y: 0.98 }, rot: 1.2, ease: 'linear' })
      key(b.launchMs + A * D.landFrac - 1, LONG_SNAP_IMAGE, { offset: { x: LONG_SNAP_OFF.to }, scale: { x: LONG_SNAP_OFF.sx, y: 0.99 }, rot: 1.5, ease: 'step' })
      // 着地: 口を開け直して低く構え直す。着地までに構えの描き位置へ戻す (床に着いてからは動かさない)。
      key(b.launchMs + A * D.landFrac, 'bite_windup_deep', { offset: { x: LONG_SNAP_OFF.crouchFrom, y: -4 }, scale: { x: W.sx, y: 0.99 }, rot: -3.5, ease: 'linear' })
      key(b.landMs, 'bite_windup_deep', { offset: { x: W.off, y: 6 }, scale: { x: W.sx, y: 0.93 }, rot: 0.4, ease: 'easeOut' })
      if (k < times.length - 1) {
        key(times[k + 1]!.launchMs - 50, 'bite_windup_deep', { offset: { x: W.off, y: 6 }, scale: { x: W.sx, y: 0.93 }, rot: 0.6, ease: 'easeIn' })
      }
    })
  } else times.forEach((b, k) => {
    const A = D.airMs
    launchAndReach(b.launchMs, A, D.launchFrac, D.reachFrac, false)
    key(b.snapMs - 1, POUNCE_REACH_IMAGE, { offset: { x: S.off }, scale: { x: S.sx, y: 0.98 }, rot: -0.6, ease: 'step' })
    // 宙で顎を閉じる (配布版の暴噛と同じ絵・同じ体)。口だけが閉じる。
    key(b.snapMs, 'bite_contact', { offset: { x: S.off }, scale: { x: S.sx, y: 0.98 }, rot: 1.2, ease: 'linear' })
    key(b.launchMs + A * D.stepFrac - 1, 'bite_contact', { offset: { x: S.off }, scale: { x: S.sx, y: 0.99 }, rot: 1.5, ease: 'step' })
    // 着地の手前: 顎を閉じたまま前脚を下ろす (bite_step)。体の伸びが少し戻る。
    key(b.launchMs + A * D.stepFrac, 'bite_step', { offset: { x: POUNCE_SWITCH_OFF.stepFrom, y: 0 }, scale: { x: 1.0, y: 0.99 }, rot: 0.6, ease: 'easeOut' })
    key(b.launchMs + A * D.landFrac - 1, 'bite_step', { offset: { x: POUNCE_SWITCH_OFF.stepFrom - 4, y: 0 }, scale: { x: 1.0, y: 0.99 }, rot: 0.2, ease: 'step' })
    // 着地: 口を開け直して低く構え直す。着地までに構えの描き位置へ戻す (床に着いてからは動かさない)。
    key(b.launchMs + A * D.landFrac, 'bite_windup_deep', { offset: { x: POUNCE_SWITCH_OFF.crouchFrom, y: -4 }, scale: { x: W.sx, y: 0.99 }, rot: -3.5, ease: 'linear' })
    key(b.landMs, 'bite_windup_deep', { offset: { x: W.off, y: 6 }, scale: { x: W.sx, y: 0.93 }, rot: 0.4, ease: 'easeOut' })
    key(b.landMs + 60, 'bite_windup_deep', { offset: { x: W.off, y: 3 }, scale: { x: W.sx, y: 0.97 }, rot: 0, ease: 'easeInOut' })
    if (k < times.length - 1) {
      key(times[k + 1]!.launchMs - 50, 'bite_windup_deep', { offset: { x: W.off, y: 6 }, scale: { x: W.sx, y: 0.93 }, rot: 0.6, ease: 'easeIn' })
    }
  })
  // 3回目の構え: 2回目より低く深く沈む (合図 = 構え直した所)。最後の stillMs は完全に止まる。
  key(plan.signalMs, 'bite_windup_deep', { offset: { x: W.off, y: 7 }, scale: { x: W.sx, y: 0.91 }, rot: 0.9, ease: 'easeOut' })
  key(plan.lungeStartMs - D.stillMs, 'bite_windup_deep', { offset: { x: W.off, y: 9 }, scale: { x: W.sx, y: 0.89 }, rot: 1.1, ease: 'step' })
  key(plan.lungeStartMs - 1, 'bite_windup_deep', { offset: { x: W.off, y: 9 }, scale: { x: W.sx, y: 0.89 }, rot: 1.1, ease: 'step' })
  // 3回目の跳び: 見せの跳びと同じ形 (口を開けて首を伸ばしたまま長く宙を進み、主人公の所で顎を閉じる = active の頭)。
  const AF = D.finalAirMs
  launchAndReach(plan.lungeStartMs, AF, D.finalLaunchFrac, D.finalReachFrac, true)
  key(long ? plan.lungeStartMs + AF * D.finalReachFrac + AWOKEN_POUNCE_LONG.settleOffMs : plan.lungeStartMs + AF * 0.4, POUNCE_REACH_IMAGE, { offset: { x: S.off }, scale: { x: S.sx + 0.02, y: 0.97 }, rot: -1.0, ease: 'linear' })
  key(plan.chargeMs - 1, POUNCE_REACH_IMAGE, { offset: { x: S.off }, scale: { x: S.sx + 0.02, y: 0.97 }, rot: -0.4, ease: 'step' })

  // 当たり (active 250ms): 顎を閉じた絵 (当たりの口)。宙の残り (60ms) で着地し、首を振って噛みしめる (足元を軸に傾けるだけ = 足は動かない)。
  const F = POUNCE_FINAL_SNAP
  const landAt = (AF * (1 - D.finalSnapFrac)) / POUNCE_FINISHER_ACTIVE_MS
  const act = (a: number, m: Motion & { image?: string }) =>
    keys.push({ phase: 'active', at: a, image: m.image ?? 'bite_contact', ...HOLD, ...m })
  act(0, { tag: 'contact', offset: { x: F.offsetX }, scale: { x: F.scaleX, y: F.scaleY }, rot: F.rot, ease: 'easeOut' })
  act(landAt, { offset: { x: S.off, y: 2 }, scale: { x: S.sx, y: 0.95 }, rot: 1.6, ease: 'easeInOut' })
  act(0.5, { offset: { x: S.off, y: 1 }, scale: { x: S.sx, y: 0.97 }, rot: 0.9, ease: 'easeInOut' })
  act(0.75, { offset: { x: S.off, y: 1 }, scale: { x: S.sx, y: 0.97 }, rot: 1.5, ease: 'easeInOut' })
  // 戻り (750ms): もう一度振ってから、配布版の暴噛の戻りと同じ並び (bite_step → bite_follow → bite_recover)。
  //   床に着いている間は絵ごとの値を保つ (step)。描き位置は絵の替わりで足の跳びが前後に割れる値 (後ろ足だけ揃えると前足が大きく跳ぶ)。
  const rec = (a: number, image: string, m: Motion) => keys.push({ phase: 'recovery', at: a, image, ...HOLD, ...m })
  rec(0, 'bite_contact', { offset: { x: S.off, y: 1 }, scale: { x: S.sx, y: 0.97 }, rot: 1.0, ease: 'easeInOut' })
  rec(0.14, 'bite_contact', { offset: { x: S.off, y: 0 }, scale: { x: S.sx, y: 0.98 }, rot: 1.4, ease: 'step' })
  rec(0.28, 'bite_step', { tag: 'followThrough', offset: { x: -18 }, rot: 0.8, ease: 'step' })
  rec(0.42, 'bite_follow', { offset: { x: -10 }, rot: 0.2, ease: 'step' })
  keys.push({ phase: 'recovery', at: 0.6, image: 'bite_recover', tag: 'recover', ease: 'easeOut' })

  timelineCache.set(plan, keys)
  return keys
}
